import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { AdjustOnlyVisual } from "@/js/pages/product/rackbuilder/constants"
import { getRackModel } from "@/js/pages/product/rackbuilder/modelPaths"
import { RackOptions } from "@/js/pages/product/rackbuilder/RackTypes"
export { createTrolleyClip, createLatBarClip, createLowRowClip, createFootplate } from "../ftSharedParts"
import { getFlipX } from "../../ftAdjustments"

// FM Parts — all part definitions for FM-6 and FM-HR

// MARK: Context
export interface FMContext {
  config: RackOptions
  materials: MaterialInfo
  height: RackOptions['height']
  edition: RackOptions['edition']
  isHalfRack: boolean
}

// MARK: Side Meshes
export function createSideMeshes(context: FMContext, left: boolean): PartDefinition[] {
  const { config, materials, height } = context
  const sideMeshes: PartDefinition[] = []
  
  sideMeshes.push({
    mesh        : getRackModel('FM_CABLES', `side pulley ${height}`),
    adjustments : getFlipX(left),
    materials,
    slots       : {
      pulleys: {
        mesh        : getRackModel('FM', 'side pulleys'),
        materials,
        adjustments : getFlipX(left),
        slots       : {},
      },
    },
  })
  
  if (config.ftShrouds) {
    sideMeshes.push({
      mesh        : getRackModel('FM', `shroud ${height}`),
      slots       : {},
      adjustments : getFlipX(left),
    })
    
    sideMeshes.push({
      mesh  : getRackModel('FM', `shroud cutout ${left ? 'left' : 'right'}`),
      slots : {},
    })
  }
  
  return sideMeshes
}

// MARK: Front Bottom Pulley
export function createFrontBottomPulley(
  context: FMContext, 
  left: boolean,
  trolleyClip: PartDefinition,
  carriage: PartDefinition
): PartDefinition {
  const { materials, isHalfRack } = context
  
  const mesh = isHalfRack
    ? getRackModel('FM', 'halfrack bottom pulley')
    : getRackModel('FM_SHARED', 'bottom front pulley')

  return {
    mesh,
    adjustments : getFlipX(left),
    materials,
    slots       : { front: carriage },
  }
}

// MARK: Resistance System
export function createResistanceByType(context: FMContext, latBarClip: PartDefinition): PartDefinition {
  const { edition } = context
  
  if (edition === 'plate loaded footplate') {
    return buildPlateResistance(context, latBarClip)
  }

  const stackType = edition === 'stack 250' ? '250' : '300'
  
  return buildStackResistance(context, stackType, latBarClip)
}

function buildPlateResistance(context: FMContext, latBarClip: PartDefinition): PartDefinition {
  const { materials, height } = context

  const top: PartDefinition[] = [{
    mesh  : getRackModel('FM_SHARED', 'top ftx'),
    materials,
    slots : {
      front: {
        mesh  : getRackModel('FM_SHARED', 'lat pulldown'),
        materials,
        slots : { attachment_clip: latBarClip },
      },
    },
  }]

  const backPost: PartDefinition[] = [{
    mesh  : getRackModel('FM', `back post ${height}`),
    materials,
    slots : {
      top,
      cables: {
        mesh  : getRackModel('FM_CABLES', `rhino ${height} ftx cables`),
        slots : {},
      },
    },
  }]

  return {
    mesh  : getRackModel('FM_SHARED', 'bottom'),
    materials,
    slots : {
      beam   : backPost,
      pulley : {
        mesh  : getRackModel('FM_SHARED', 'back pulley'),
        materials,
        slots : {},
      },
    },
  }
}

// FM-6 uses its own multi-part weight stack assembly — not FM_SHARED's combined mesh (see functionalTrainer/CLAUDE.md)
function buildStackResistance(context: FMContext, stackType: string, latBarClip: PartDefinition): PartDefinition {
  const { config, materials, height } = context

  const top: PartDefinition[] = [{
    mesh  : getRackModel('FM', 'stack top'),
    materials,
    slots : {
      front: {
        mesh  : getRackModel('FM_SHARED', 'lat pulldown'),
        materials,
        slots : { attachment_clip: latBarClip },
      },
    },
  }]

  const beam: PartDefinition[] = [{
    mesh  : getRackModel('FM', `stack ${height}`),
    materials,
    slots : { top },
  }]

  if (config.ftShrouds) {
    beam.push({
      mesh  : getRackModel('FM', `stack shroud ${height}`),
      slots : {},
    })
  }

  const stackTopSlot = stackType === '250' ? '250_stack' : '300_stack'

  return {
    mesh  : getRackModel('FM', 'stack bottom'),
    materials,
    slots : {
      beam,
      cable: {
        mesh  : getRackModel('FM_CABLES', 'bottom stack cable'),
        slots : {},
      },
      [stackTopSlot]: {
        mesh  : getRackModel('FM', 'weights top'),
        materials,
        slots : {
          cable: {
            mesh  : getRackModel('FM_CABLES', 'top stack cable'),
            slots : {},
          },
        },
      },
    },
  }
}

// MARK: Back Bottom Pulley
export function createBackBottomPulley(context: FMContext, left: boolean): PartDefinition {
  const { materials } = context
  
  return {
    mesh        : getRackModel('FM', 'bottom back pulley'),
    adjustments : getFlipX(left),
    materials,
    slots       : {},
  }
}

// MARK: Rails System
export function createTopRailPulley(context: FMContext, depth: string, left: boolean): PartDefinition {
  const { materials } = context
  const topBackPulley = buildTopBackPulley(context, left)

  const topRail: PartDefinition = {
    mesh        : getRackModel('FM', `top rail ${depth}`),
    materials,
    slots       : { back: [topBackPulley] },
    adjustments : left ? [] : [
      { type: 'rotate', axis: 'Y', amount: 180 },
      { type: 'flip', axis: 'X' },
    ],
    adjustmentType: left ? undefined : AdjustOnlyVisual,
  }

  return {
    mesh        : getRackModel('FM_SHARED', 'top front pulley'),
    materials,
    slots       : { rail: topRail },
    adjustments : getFlipX(left),
  }
}

function buildTopBackPulley(context: FMContext, left: boolean): PartDefinition {
  const { materials } = context
  
  return {
    mesh  : getRackModel('FM', 'top back pulley'),
    materials,
    slots : {
      plate: {
        mesh        : getRackModel('FM', `top back pulley cover ${left ? 'left' : 'right'}`),
        slots       : {},
        adjustments : getFlipX(left),
      },
    },
  }
}

export function createBottomRail(context: FMContext, depth: string, left: boolean): PartDefinition {
  const { materials } = context
  
  return {
    mesh        : getRackModel('FM', `bottom rail ${depth}`),
    materials,
    slots       : {},
    anchor      : 'main',
    adjustments : left ? [] : [
      { type: 'rotate', axis: 'Y', amount: 180 },
      { type: 'flip', axis: 'X' },
    ],
    adjustmentType: AdjustOnlyVisual,
  }
}

// MARK: Half Rack Top Pulley
export function createHalfRackTopPulleyCover(left: boolean): PartDefinition {
  return {
    mesh        : getRackModel('FM', `halfrack top pulley cover ${left ? 'left' : 'right'}`),
    adjustments : getFlipX(left),
    slots       : {},
  }
}

export function createHalfRackTopPulley(context: FMContext, left: boolean): PartDefinition {
  const { materials } = context
  
  return {
    mesh        : getRackModel('FM', 'halfrack top pulley'),
    slots       : { plate: createHalfRackTopPulleyCover(left) },
    materials,
    offset      : { x: 0, z: 2 },
    adjustments : getFlipX(left),
  }
}


// MARK: Rear Crossmember
export function createRearCrossmember(
  context: FMContext, 
  footplate: PartDefinition, 
  resistance: PartDefinition
): PartDefinition {
  const { materials } = context
  
  return {
    mesh  : getRackModel('FM_SHARED', 'rear crossmember'),
    materials,
    slots : { footplate, back: [resistance] },
  }
}